import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Icon, pushSupported, pushPermission, enablePush, disablePush,
  isPushSubscribed, getPushPreferences, savePushPreferences, sendTestPush,
} from "../lib/core";

const CATEGORIES = [
  {
    id: "purchase",
    icon: "receipt_long",
    title: "Purchase updates",
    desc: "When an airtime or data top-up succeeds or fails.",
  },
  {
    id: "wallet",
    icon: "account_balance_wallet",
    title: "Wallet funding",
    desc: "When your wallet is funded or a refund lands.",
  },
  {
    id: "auto_reminders",
    icon: "autorenew",
    title: "Auto top-ups & reminders",
    desc: "Family allowance heads-ups, auto-recharge results & reminders.",
  },
  {
    id: "referrals_rewards",
    icon: "card_giftcard",
    title: "Referrals & rewards",
    desc: "New referrals, referral payouts and cashback earned.",
  },
  {
    id: "promotions",
    icon: "campaign",
    title: "Promotions & offers",
    desc: "Exclusive deals, perks and occasional announcements.",
  },
];

const Toggle = ({ checked, onChange, disabled, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    data-testid={testid}
    className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-200 ${
      checked ? "bg-primary-custom" : "bg-slate-300"
    } ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

export const NotificationSettingsPage = ({ onBack }) => {
  const [prefs, setPrefs] = useState(null);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const supported = pushSupported();
  const perm = pushPermission();

  useEffect(() => {
    (async () => {
      try {
        const [p, sub] = await Promise.all([getPushPreferences(), isPushSubscribed()]);
        setPrefs(p);
        setSubscribed(sub);
      } catch {
        setPrefs({ purchase: true, wallet: true, auto_reminders: true, referrals_rewards: true, promotions: true });
      }
    })();
  }, []);

  const toggleMaster = async () => {
    setBusy(true);
    try {
      if (subscribed) {
        await disablePush();
        setSubscribed(false);
        toast.success("Push turned off on this device");
      } else {
        await enablePush();
        setSubscribed(true);
        toast.success("Notifications enabled on this device 🎉");
      }
    } catch (e) {
      toast.error(e.message || "Couldn't update notifications");
    } finally {
      setBusy(false);
    }
  };

  const toggleCategory = async (id, value) => {
    const next = { ...prefs, [id]: value };
    setPrefs(next);
    try {
      await savePushPreferences({ [id]: value });
    } catch {
      setPrefs((p) => ({ ...p, [id]: !value }));
      toast.error("Couldn't save that change");
    }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      await sendTestPush();
      toast.success("Test notification sent — check your device 🔔");
    } catch {
      toast.error("Couldn't send a test notification");
    } finally {
      setTesting(false);
    }
  };

  const denied = perm === "denied";

  return (
    <div className="min-h-screen bg-background-light" data-testid="notification-settings-page">
      <header className="bg-primary-custom text-white sticky top-0 z-50" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={onBack} data-testid="notif-settings-back" className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <Icon name="arrow_back" />
          </button>
          <span className="font-bold text-xl">Notifications</span>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6 pb-24">
        {/* Device master toggle */}
        <section className="bg-white rounded-2xl border border-slate-200/70 p-5">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-primary-custom/10 flex items-center justify-center shrink-0">
              <Icon name="notifications_active" className="text-primary-custom" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-slate-900">Push on this device</p>
              <p className="text-sm text-slate-500">
                {supported
                  ? subscribed
                    ? "You'll get alerts even when the app is closed."
                    : "Turn on to receive alerts on this device."
                  : "Not supported on this browser."}
              </p>
            </div>
            {supported && !denied && (
              <Toggle checked={subscribed} onChange={toggleMaster} disabled={busy} testid="push-master-toggle" />
            )}
          </div>

          {!supported && (
            <div className="mt-4 text-xs bg-slate-50 text-slate-500 rounded-xl p-3 leading-relaxed" data-testid="push-unsupported-note">
              On iPhone, add TopIt to your Home Screen first (Share → Add to Home Screen), then open it from there to enable notifications.
            </div>
          )}
          {denied && (
            <div className="mt-4 text-xs bg-amber-50 text-amber-700 rounded-xl p-3 leading-relaxed" data-testid="push-denied-note">
              Notifications are blocked for TopIt. Enable them in your browser/site settings, then come back here.
            </div>
          )}

          {subscribed && (
            <button
              onClick={runTest}
              disabled={testing}
              data-testid="push-test-btn"
              className="mt-4 w-full h-11 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Icon name={testing ? "progress_activity" : "send"} className={`text-base ${testing ? "animate-spin" : ""}`} />
              {testing ? "Sending…" : "Send a test notification"}
            </button>
          )}
        </section>

        {/* Per-category alerts */}
        <section className="bg-white rounded-2xl border border-slate-200/70 divide-y divide-slate-100">
          <div className="px-5 pt-5 pb-3">
            <p className="font-bold text-slate-900">Alert types</p>
            <p className="text-sm text-slate-500">Choose which push alerts you want to receive.</p>
          </div>
          {!prefs ? (
            <div className="px-5 py-10 flex justify-center">
              <Icon name="progress_activity" className="animate-spin text-slate-300 text-2xl" />
            </div>
          ) : (
            CATEGORIES.map((c) => (
              <div key={c.id} className="px-5 py-4 flex items-center gap-4" data-testid={`notif-cat-${c.id}`}>
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                  <Icon name={c.icon} className="text-slate-500 text-xl" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 text-sm">{c.title}</p>
                  <p className="text-xs text-slate-500">{c.desc}</p>
                </div>
                <Toggle
                  checked={!!prefs[c.id]}
                  onChange={(v) => toggleCategory(c.id, v)}
                  testid={`notif-toggle-${c.id}`}
                />
              </div>
            ))
          )}
        </section>

        <p className="text-xs text-slate-400 px-1 leading-relaxed">
          These switches control push alerts only. In-app notifications and email receipts are always on.
        </p>
      </main>
    </div>
  );
};
